import {
  Building2,
  CirclePlay,
  Handshake,
  House,
  Search,
  Users,
  type LucideIcon,
} from "lucide-react";
import { CanAccess, useGetIdentity, useTranslate } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";

import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { cn } from "@/lib/utils";
import {
  ChangelogMenuItem,
  ImportFromJsonMenuItem,
  ProfileMenu,
  SettingsMenu,
  UsersMenu,
} from "../layout/Header";
import { useConfigurationContext } from "../root/ConfigurationContext";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Route patterns that mark the item as current. */
  match: string[];
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

/** Desktop navigation: a light sidebar grouped by what the sales rep is doing. */
export const AppSidebar = () => {
  const translate = useTranslate();
  const location = useLocation();
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();

  const groups: NavGroup[] = [
    {
      title: translate("crm.construnario.nav.work"),
      items: [
        {
          to: "/",
          label: translate("crm.construnario.nav.home"),
          icon: House,
          match: ["/"],
        },
      ],
    },
    {
      title: translate("crm.construnario.nav.portfolio"),
      items: [
        {
          to: "/companies",
          label: translate("resources.companies.name", { smart_count: 2 }),
          icon: Building2,
          match: ["/companies/*"],
        },
        {
          to: "/contacts",
          label: translate("resources.contacts.name", { smart_count: 2 }),
          icon: Users,
          match: ["/contacts/*"],
        },
        {
          to: "/deals",
          label: translate("resources.deals.name", { smart_count: 2 }),
          icon: Handshake,
          match: ["/deals/*"],
        },
      ],
    },
    {
      title: translate("crm.construnario.nav.lookup"),
      items: [
        {
          to: "/directory",
          label: translate("crm.construnario.directory.menu"),
          icon: Search,
          match: ["/directory"],
        },
        {
          to: "/help",
          label: translate("crm.construnario.help.menu"),
          icon: CirclePlay,
          match: ["/help"],
        },
      ],
    },
  ];

  const isCurrent = (item: NavItem) =>
    item.match.some((pattern) => matchPath(pattern, location.pathname));

  return (
    <aside className="sticky top-0 flex h-screen flex-col gap-5 border-r border-sidebar-border bg-sidebar px-3 py-4 text-sidebar-foreground">
      <Link to="/" className="flex items-center gap-2 px-2 no-underline">
        <img className="[.light_&]:hidden h-5" src={darkModeLogo} alt="" />
        <img className="[.dark_&]:hidden h-5" src={lightModeLogo} alt="" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {title}
        </span>
      </Link>

      {groups.map((group) => (
        <nav
          key={group.title}
          aria-label={group.title}
          className="flex flex-col gap-0.5"
        >
          <span className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
            {group.title}
          </span>
          {group.items.map((item) => {
            const current = isCurrent(item);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={current ? "page" : undefined}
                className={cn(
                  "flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm no-underline transition-colors",
                  current
                    ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                    : "hover:bg-secondary hover:text-foreground",
                )}
              >
                <Icon className="size-4 shrink-0" strokeWidth={1.8} />
                {item.label}
              </Link>
            );
          })}
        </nav>
      ))}

      <SidebarUser />
    </aside>
  );
};

const SidebarUser = () => {
  const { identity } = useGetIdentity();
  return (
    <div className="mt-auto flex items-center gap-2 border-t border-sidebar-border pt-3">
      <UserMenu>
        <ProfileMenu />
        <CanAccess resource="sales" action="list">
          <UsersMenu />
        </CanAccess>
        <CanAccess resource="configuration" action="edit">
          <SettingsMenu />
        </CanAccess>
        <ImportFromJsonMenuItem />
        <ChangelogMenuItem />
      </UserMenu>
      <span className="min-w-0 truncate text-sm text-foreground">
        {identity?.fullName}
      </span>
    </div>
  );
};

/** Top bar of the desktop layout: global search entry point and app actions. */
export const AppTopBar = () => {
  const translate = useTranslate();
  return (
    <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-card px-6 py-2.5">
      <Link
        to="/directory"
        className="flex max-w-lg flex-1 items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground no-underline hover:border-muted-foreground"
      >
        <Search className="size-4" strokeWidth={1.8} />
        {translate("crm.construnario.search.placeholder")}
      </Link>
      <div className="ml-auto flex items-center gap-1">
        <ThemeModeToggle />
        <RefreshButton />
      </div>
    </header>
  );
};
